/**
 * NotificationBell — Top-bar alerts inbox.
 *
 * Polls `/api/notifications` every 30 seconds and shows an unread
 * badge. Clicking the bell opens a popover with the 20 most recent
 * alerts. Each row is click-through (deep-links to the underlying
 * record and marks the alert read).
 *
 * Alerts are written server-side by:
 *   • Communication triggers with a `push` channel
 *   • The scheduler detector jobs — overdue surveys, freshly
 *     critical sites, delivery failures
 *   • Any code that calls `alerts.create_alert(...)`
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent as PopoverContentBase, PopoverTrigger as PopoverTriggerBase } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { Bell, Check, AlertTriangle, ShieldAlert, MailWarning, Info, Loader2 } from 'lucide-react';

/* Cast shims — the shadcn popover is a .jsx wrapper without prop typings. */
const PopoverContent = PopoverContentBase as unknown as React.FC<{ children?: React.ReactNode; align?: string; sideOffset?: number; className?: string; 'data-testid'?: string }>;
const PopoverTrigger = PopoverTriggerBase as unknown as React.FC<{ children?: React.ReactNode; asChild?: boolean }>;

interface NotificationDoc {
  id: string;
  title: string;
  body?: string;
  severity?: 'info' | 'warning' | 'critical' | string;
  category?: string;
  url?: string | null;
  read: boolean;
  created_at: string;
}

interface NotificationsPayload {
  items: NotificationDoc[];
  unread_count: number;
}

const POLL_MS = 60_000; // 60s — halves backend load vs 30s poll while still feeling live enough for overdue/critical alerts.

/* ─── Severity/category → chip colour + icon ──────────────────── */
function severityStyle(sev?: string): { dot: string; icon: React.ReactNode } {
  switch ((sev || '').toLowerCase()) {
    case 'critical':
      return { dot: 'bg-red-600',    icon: <ShieldAlert size={14} className="text-red-600" /> };
    case 'warning':
      return { dot: 'bg-amber-500',  icon: <AlertTriangle size={14} className="text-amber-500" /> };
    default:
      return { dot: 'bg-raptor-blue', icon: <Info size={14} className="text-raptor-blue" /> };
  }
}
function categoryIcon(cat?: string) {
  switch ((cat || '').toLowerCase()) {
    case 'delivery_failure': return <MailWarning size={14} />;
    case 'critical_site':    return <ShieldAlert size={14} />;
    case 'overdue_survey':   return <AlertTriangle size={14} />;
    default:                  return <Info size={14} />;
  }
}

/* ─── Relative time (compact) ─────────────────────────────────── */
function timeAgo(iso: string): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NotificationsPayload>({ items: [], unread_count: 0 });
  const [mutedCategories, setMutedCategories] = useState<Set<string>>(new Set());
  const nav = useNavigate();

  const loadPrefs = useCallback(async () => {
    try {
      const r = await api.get<{ muted_categories: string[] }>('/notifications/preferences');
      setMutedCategories(new Set(r.data.muted_categories || []));
    } catch { /* not fatal */ }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get<NotificationsPayload>('/notifications?limit=20');
      setData(r.data);
    } catch { /* silent — auth may not be ready */ }
    finally { setLoading(false); }
  }, []);

  /* Initial + polling. We deliberately skip the interval work when the
     browser tab is hidden — a background tab doesn't need live badges
     and shaves a big chunk of load off the backend for idle sessions.
     Preferences also load once and again whenever the Preferences page
     dispatches the `raptor:alert-prefs-changed` event. */
  useEffect(() => {
    void load();
    void loadPrefs();
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void load();
    }, POLL_MS);
    // When the operator switches back to the tab after a long idle,
    // pull once immediately so the badge is fresh without waiting up
    // to a full minute for the next tick.
    const onVis = (): void => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onVis);
    const onPrefs = () => { void loadPrefs(); };
    window.addEventListener('raptor:alert-prefs-changed', onPrefs);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('raptor:alert-prefs-changed', onPrefs);
    };
  }, [load, loadPrefs]);

  /* Also refresh whenever the popover opens (immediate feedback). */
  useEffect(() => { if (open) void load(); }, [open, load]);

  const markRead = useCallback(async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setData((d) => ({
        items: d.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unread_count: Math.max(0, d.unread_count - 1),
      }));
    } catch { /* ignore */ }
  }, []);

  const markAll = useCallback(async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setData((d) => ({ items: d.items.map((n) => ({ ...n, read: true })), unread_count: 0 }));
    } catch { /* ignore */ }
  }, []);

  const openAlert = useCallback(async (n: NotificationDoc) => {
    if (!n.read) void markRead(n.id);
    if (n.url) {
      setOpen(false);
      nav(n.url);
    }
  }, [markRead, nav]);

  /* Filter out muted categories client-side. When we mute, we also
     subtract the muted unread from the badge so the number matches
     what the operator actually sees in the popover. */
  const visibleItems = useMemo(
    () => data.items.filter((n) => !mutedCategories.has((n.category || '').toLowerCase())),
    [data.items, mutedCategories],
  );
  const mutedUnread = useMemo(
    () => data.items.filter((n) => !n.read && mutedCategories.has((n.category || '').toLowerCase())).length,
    [data.items, mutedCategories],
  );
  const visibleUnreadCount = Math.max(0, data.unread_count - mutedUnread);

  const badge = useMemo(() => (visibleUnreadCount > 99 ? '99+' : String(visibleUnreadCount)), [visibleUnreadCount]);
  const hasUnread = visibleUnreadCount > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={hasUnread ? `Notifications — ${data.unread_count} unread` : 'Notifications'}
          data-testid="notification-bell"
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-raptor-blue/60 focus-visible:outline-none transition-colors"
          style={{ color: 'var(--raptor-header-text)' }}
        >
          <Bell size={18} />
          {hasUnread && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[var(--raptor-header-bg,white)]"
              data-testid="notification-bell-badge"
            >{badge}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[380px] p-0" data-testid="notification-popover">
        <div className="p-3 border-b border-border/60 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-raptor-navy inline-flex items-center gap-2">
              <Bell size={14} /> Notifications
              {loading && <Loader2 size={12} className="animate-spin text-raptor-navy/40" />}
            </div>
            <div className="text-[11px] text-raptor-navy/60">
              {data.unread_count === 0 ? 'All caught up' : `${data.unread_count} unread`}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => void markAll()} disabled={!hasUnread} data-testid="notification-mark-all">
            <Check size={14} /> Mark all read
          </Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto" role="list">
          {visibleItems.length === 0 && (
            <div className="p-6 text-center text-xs text-raptor-navy/60">
              You have no notifications yet. Overdue surveys, freshly critical sites,
              and delivery failures will show up here as they happen.
            </div>
          )}
          {visibleItems.map((n) => {
            const sev = severityStyle(n.severity);
            return (
              <button
                type="button"
                key={n.id}
                onClick={() => void openAlert(n)}
                className={`w-full text-left p-3 border-b border-border/40 transition-colors flex gap-3 items-start ${
                  n.read ? 'bg-white hover:bg-raptor-neutral/40' : 'bg-raptor-blue/[0.04] hover:bg-raptor-blue/[0.08]'
                }`}
                data-testid={`notification-item-${n.id}`}
                role="listitem"
              >
                <span className="mt-1.5 relative inline-flex items-center justify-center">
                  <span className={`inline-block w-2 h-2 rounded-full ${sev.dot}`} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-raptor-navy/60">{categoryIcon(n.category)}</span>
                    <div className={`text-[13px] leading-tight truncate ${n.read ? 'text-raptor-navy/80' : 'font-semibold text-raptor-navy'}`}>
                      {n.title}
                    </div>
                  </div>
                  {n.body && (
                    <div className="mt-0.5 text-[11px] text-raptor-navy/60 line-clamp-2">{n.body}</div>
                  )}
                  <div className="mt-1 text-[10px] uppercase tracking-wide text-raptor-navy/40 font-tabular">
                    {timeAgo(n.created_at)} ago
                  </div>
                </div>
                {!n.read && (
                  <button
                    type="button"
                    aria-label="Mark as read"
                    onClick={(e) => { e.stopPropagation(); void markRead(n.id); }}
                    className="mt-1 text-raptor-navy/40 hover:text-raptor-navy"
                    data-testid={`notification-mark-read-${n.id}`}
                  >
                    <Check size={14} />
                  </button>
                )}
              </button>
            );
          })}
        </div>
        <div className="p-2 border-t border-border/60 bg-raptor-neutral/30 flex items-center justify-between">
          <span className="text-[11px] text-raptor-navy/50 pl-1">
            {mutedUnread > 0 && `${mutedUnread} muted`}
          </span>
          <button
            type="button"
            onClick={() => { setOpen(false); nav('/insurer/alerts'); }}
            className="text-[11px] font-semibold text-raptor-blue hover:underline px-2"
            data-testid="notification-see-all"
          >See all alerts →</button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
